import { useAppSelector } from "../utils/hooks";
import type { UserStream } from "../types/StreamTypes";
import { getCallPeerSnapshot } from "../utils/callPeerSnapshot";

/**
 * Avatar/name for a call participant.
 * Never use CurrentConversation unless it matches the active call chat —
 * otherwise switching chats while minimized poisons the call UI.
 */
export function useStreamAvatar(
  stream?: Partial<UserStream> | null,
): { url: string | null; name: string; initials: string } {
  const ConnectedUser = useAppSelector((s) => s.ConnectedUser);
  const userData = useAppSelector((s) => s.userData);
  const conversation = useAppSelector((s) => s.CurrentConversation);
  const callSnap = getCallPeerSnapshot();

  if (!stream) {
    return { url: null, name: "", initials: "?" };
  }

  let url: string | null = null;
  let name = "";

  if (stream.isLocal) {
    url = userData.profile_pic || null;
    name = userData.full_name || userData.user_name || stream.userName || "";
  } else {
    const byConnected = ConnectedUser.find((x) => x.user_id === stream.userId);
    if (byConnected) {
      url = byConnected.profile_image || null;
      name = byConnected.full_name || "";
    }

    const conversationMatchesCall =
      callSnap.chatId > 0 &&
      Number(conversation?.chat_id) === callSnap.chatId &&
      conversation?.chat_type === "private";

    if ((!url || !name) && conversationMatchesCall) {
      if (!url && conversation.profile_pic) url = conversation.profile_pic;
      if (!name && conversation.full_name) name = conversation.full_name;
    }

    // Frozen call peer (chat may have switched while call stayed active)
    if ((!url || !name) && callSnap.chatType !== "group" && callSnap.chatType !== "public_group") {
      if (!url && callSnap.avatar) url = callSnap.avatar;
      if (!name && callSnap.title) name = callSnap.title;
    }

    if (!name) name = stream.userName || "";
  }

  const cleanedName = name.replace(/^User\s+\d+$/i, "").trim();
  const finalName = cleanedName || name;

  const initials =
    (finalName || "?")
      .trim()
      .split(/\s+/)
      .slice(0, 2)
      .map((part) => part.charAt(0).toUpperCase())
      .join("") || "?";

  return { url, name: finalName, initials };
}
